import { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

// internal hooks
import { useEscape } from "../../hooks/useEscape";
import { useGetCsvData } from "../../hooks/useGetCsvData";
import { useVerifyUnique } from "../../hooks/useVerifyUnique";

// styles and images
import "../project/Project.css";
import "./VendorInvoices.css";
import Close from "../../assets/close.svg";

// global
import { userStore, projectStore, messageAndErrorStore } from "../../store/Store";

// components
import InputField from "../../formComponents/InputField";
import FormButton from "../../formComponents/FormButton";

export default function AddSandInvoice({ setTriggerInvoice }) {
  // global
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const currentCompany = userStore((data) => data.currentCompany);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const currentProject = projectStore((data) => data.currentProject);
  const projectVendorInvoices = projectStore(
    (data) => data.projectVendorInvoices
  );
  const setProjectVendorInvoices = projectStore(
    (data) => data.setProjectVendorInvoices
  );
  const setProjectVendorInvoiceTotals = projectStore(
    (data) => data.setProjectVendorInvoiceTotals
  );
  const projectLoads = projectStore((data) => data.projectLoads);
  const projectSandRates = projectStore((data) => data.projectSandRates);
  const setProjectLoads = projectStore((data) => data.setProjectLoads);
  const setProjectAssetLoads = projectStore(
    (data) => data.setProjectAssetLoads
  );
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceLoads, setInvoiceLoads] = useState(null);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [pending, setPending] = useState(false);
  const [vendorInvoice, setVendorInvoice] = useState({
    id: 0,
    project_id: currentProject.project.id,
    invoice_name: "",
    company_id: currentCompany.id,
    vendor_name: "",
    is_sand: true,
    is_asset: false,
    purchase_order: "",
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
  });

  // get options
  useEffect(() => {
    let opts = [];
    if (projectSandRates) {
      projectSandRates.forEach((r) => {
        opts.push({ value: r.po_id, label: r.po_number });
      });
    }
    setOptions(opts);
  }, [projectSandRates]);

  // parse out the file
  useGetCsvData(invoiceFile, setInvoiceLoads);
  const { exists } = useVerifyUnique(
    projectVendorInvoices.invoices,
    "invoice_name",
    vendorInvoice.invoice_name
  );

  const handleClose = () => {
    setTriggerInvoice(false);
  };

  useEscape(setTriggerInvoice, [], [], pending);

  const handleName = (e) => {
    setVendorInvoice({
      ...vendorInvoice,
      invoice_name: e.target.value,
    });
  };

  const handleSelect = (o, e) => {
    if (e.name === "purchase_order") {
      setPurchaseOrder(o);
      const idx = projectLoads.findIndex((x) => x.po_number === o.label);
      if (idx > -1) {
        setVendorInvoice({
          ...vendorInvoice,
          purchase_order: o.label,
          vendor_name:
            idx > -1
              ? projectLoads[idx].terminal_name
              : vendorInvoice.vendor_name,
        });
      }
    }
  };

  const handleFile = (e) => {
    let f = e.target.files[0];
    if (!f.type.includes("text/csv")) {
      toggleNewMessage("Please use the customer invoice template csv file");
      return;
    }
    setInvoiceFile(f);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);
    var tickets = [];
    if (exists) {
      toggleNewMessage("That invoice name already exists");
      setPending(false);
      return;
    }
    // update load data
    const loads = [];
    for (var i = 1; i < invoiceLoads.length; i++) {
      if (invoiceLoads[0].errors.length > 0) {
        toggleNewMessage("There was an issue parsing out the uploaded file");
        setPending(false);
        return;
      } else if (invoiceLoads[i].errors.length > 0) {
        toggleNewMessage("There was an issue parsing out the uploaded file");
        setPending(false);
        return;
      }
      // get the load id from project loads
      const ticket_no = invoiceLoads[i].data[0];
      tickets.push(invoiceLoads[i].data[0]);
      var idx = 0;
      var ticketCount = 0;
      const projectLoad = projectLoads.filter(
        (x) =>
          x.ticket_number === ticket_no &&
          x.po_number === vendorInvoice.purchase_order
      );
      ticketCount = tickets.filter((x) => x === ticket_no).length;
      if (projectLoad.length >= ticketCount) {
        idx = ticketCount - 1;
      }

      if (projectLoad.length === 0 || projectLoad.length < ticketCount) {
        loads.push({
          load_no: 0,
          weight_tons: parseFloat(invoiceLoads[i].data[3]),
          moisture_content: parseFloat(invoiceLoads[i].data[2]),
          load_id: 0,
          mileage: 0,
          carrier_name: "",
          pc_carrier_id: "",
          pc_driver_id: "",
          driver_name: "",
          terminal_name: "",
          po_number: "",
          product_name: "",
          ticket_number: invoiceLoads[i].data[0],
          delivered_date: "2012-02-21T08:02:00-07:00",
          freight_cost: 0,
          freight_rev: 0,
          fsc_cost: 0,
          fsc_rev: 0,
          sand_cost:  parseFloat(invoiceLoads[i].data[1]),
          sand_rev: 0,
          load_is_missing: true,
          duplicate_of: 0,
          created_by_id: currentUser.id,
          updated_by_id: currentUser.id,
        });
      } else {
        loads.push({
          load_no: projectLoad[idx].load_no,
          weight_tons: parseFloat(invoiceLoads[i].data[3]),
          moisture_content: parseFloat(invoiceLoads[i].data[2]),
          load_id: projectLoad[idx].id,
          mileage: projectLoad[idx].approved_mileage,
          carrier_name: projectLoad[idx].carrier_name,
          pc_carrier_id: projectLoad[idx].pc_carrier_id,
          pc_driver_id: projectLoad[idx].pc_driver_id,
          driver_name: projectLoad[idx].driver_name,
          terminal_name: projectLoad[idx].terminal_name,
          po_number: projectLoad[idx].po_number,
          product_name: projectLoad[idx].product_name,
          ticket_number: projectLoad[idx].ticket_number,
          delivered_date: projectLoad[idx].delivered_date,
          freight_cost: 0,
          freight_rev: 0,
          fsc_cost: 0,
          fsc_rev: 0,
          sand_cost:parseFloat(invoiceLoads[i].data[1]),
          sand_rev: projectLoad[idx].sand_rev,
          load_is_missing: false,
          duplicate_of: projectLoad[idx].sand_invoice_id,
          created_by_id: currentUser.id,
          updated_by_id: currentUser.id,
        });
      }
    }

    const reqBody = {
      invoice: vendorInvoice,
      loads: loads,
      project_id: currentProject.project.id,
    };
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);

    const requestOptions = {
      method: "POST",
      headers,
      body: JSON.stringify(reqBody),
      signal: controller.signal,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/vendor-invoice/${vendorInvoice.id}`,
      requestOptions
    )
      .then((res) => {
        if (res.status === 401) {
          navigate("/login");
          setAuthStatus(false);
          setPending(false);
          setTriggerInvoice(false);
          return;
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data.error) {
          if (data.message === "project is archived and cannot be updated") {
            toggleNewMessage("Project is Archived and cannot be updated");
            setPending(false);
            return;
          } else {
            toggleNewMessage("Something went wrong");
            setPending(false);
            return;
          }
        } else {
          toggleNewMessage(null, "Invoice Added");
          setProjectVendorInvoices(data.invoice_data);
          setProjectLoads(data.project_loads);
          setProjectAssetLoads(data.project_asset_loads);
          setProjectVendorInvoiceTotals(data.vendor_invoice_totals);
          setPending(false);
          setTriggerInvoice(false);
        }
      })
      .catch((_) => {
        toggleNewMessage("Something went wrong");
        setPending(false);
        return;
      })
      .finally(() => setPending(false));
    return () => controller.abort();
  };

  return (
    <div className="popup">
      <div className="popup-inner">
        {!pending && (
          <div className="close">
            <img onClick={handleClose} src={Close} alt="close" />
          </div>
        )}
        <h3>Add Sand Invoice</h3>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <InputField
              name={"invoice_name"}
              type={"text"}
              title={"Name"}
              placeholder={"Invoice Name"}
              onChange={handleName}
              value={vendorInvoice.invoice_name}
              required={true}
            />
          </div>
          <div className="row g-3">
            <span>Purchase Order</span>
            <Select
              id="purchase_order"
              options={options}
              onChange={handleSelect}
              placeholder="Select PO"
              value={purchaseOrder}
              className="select-box"
              name="purchase_order"
            />
          </div>
          <div className="row g-3">
            <InputField
              name={"invoice_file"}
              type={"file"}
              title={"Vendor Backup"}
              placeholder={"Choose File"}
              onChange={handleFile}
              required={true}
            />
          </div>
          <div className="row g-3">
            <div className="col">
              <FormButton name={"Submit"} pending={pending} />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
